import React from "react";
import { cn } from "@/lib/utils";

/**
 * Table — data-table primitive.
 *
 * Design language:
 *   • Zebra striping via `.tr:nth-child(even)` — subtle warm neutral.
 *   • Header row uses `bg-surface-muted` + uppercase caption-style text.
 *   • Border-collapse; hairline dividers only. NO heavy row shadows.
 *
 * Composition:
 *   <Table>
 *     <THead><TR><TH>…</TH></TR></THead>
 *     <TBody><TR><TD>…</TD></TR></TBody>
 *   </Table>
 *
 * All sub-components accept `className` for consumer-side overrides.
 */

export function Table({
  className,
  "data-testid": testId,
  children,
  caption,
  ...rest
}) {
  return (
    <div
      className={cn(
        "overflow-x-auto rounded-[var(--ds-radius-md)] border border-[var(--ds-color-border)]"
      )}
    >
      <table
        role="table"
        data-testid={testId}
        className={cn("w-full border-collapse text-[14px]", className)}
        {...rest}
      >
        {caption && (
          <caption className="sr-only">{caption}</caption>
        )}
        {children}
      </table>
    </div>
  );
}

export function THead({ className, children, ...rest }) {
  return (
    <thead
      className={cn("bg-[var(--ds-color-surface-muted)]", className)}
      {...rest}
    >
      {children}
    </thead>
  );
}

export function TBody({ className, children, ...rest }) {
  return (
    <tbody className={className} {...rest}>
      {children}
    </tbody>
  );
}

export function TR({ className, children, ...rest }) {
  return (
    <tr
      className={cn(
        "border-t border-[var(--ds-color-border)]",
        "even:bg-[var(--ds-color-surface)] odd:bg-[var(--ds-color-surface)]",
        className
      )}
      {...rest}
    >
      {children}
    </tr>
  );
}

export function TH({ className, children, scope = "col", ...rest }) {
  return (
    <th
      scope={scope}
      className={cn(
        "px-4 py-2.5 text-left font-semibold uppercase tracking-wide",
        "text-[12px] text-[var(--ds-color-ink-muted)]",
        className
      )}
      {...rest}
    >
      {children}
    </th>
  );
}

export function TD({ className, children, ...rest }) {
  return (
    <td
      className={cn(
        "px-4 py-3 align-middle text-[var(--ds-color-ink)]",
        className
      )}
      {...rest}
    >
      {children}
    </td>
  );
}

export default Table;
